/**
 * SB2-W-02 — Claim & Edit (now wired to the new POST /claim endpoint).
 *
 * Decision matrix (locked per OQ-SB2-B1(c) + OQ-SB2-B3(a) +
 * Christian's storybook §5 prose):
 *
 * | Caller state                                     | Button       | Behaviour                                              |
 * |--------------------------------------------------|--------------|--------------------------------------------------------|
 * | Dataset assigned to me                           | Edit         | Navigate to /analysis/:id (editable)                   |
 * | Dataset unassigned + I'm claim-eligible + state  | Claim & Edit | POST /claim → 200 → navigate to /analysis/:id          |
 * |   ∈ {PRE_SCREENING, EXPERT_ANALYSIS}             |              |                                                        |
 * | Dataset assigned to someone else                 | View         | Navigate to /analysis/:id (read-only)                  |
 * | Caller not claim-eligible                        | View         | Navigate to /analysis/:id (read-only)                  |
 * | Completed / not-claimable state                  | View         | Navigate to /analysis/:id (read-only)                  |
 *
 * Claim-eligible set (LOCAL — narrower than R58 workflow-role set):
 *   {platform_admin, tenant_admin, expert_analyst, pre_screener}
 *
 * Pre-SB2: this button hit PATCH /data-acquisitions/{id}/assignee with
 * `{userId: myUserId}` which 5xx'd against the live BE (see SB2-W-02
 * §0 hotfix block + transcript [00:02:37]). SB2 introduces a dedicated
 * POST /data-acquisitions/{id}/claim that locks the row + writes an
 * audit row in one call.
 *
 * Toast mapping per SB2-W-02 §3.3:
 *   - 200 → none (optimistic; navigate immediately)
 *   - 409 data_acquisition_already_locked → "Already claimed by {name}."
 *   - 403 → structured-403 `reason` verbatim
 *   - 5xx → "Couldn't claim. Try again." (no retry button in 1.0; FE re-issues on next click)
 */
import { useState, type ReactElement } from "react";
import { Button, Snackbar, Alert } from "@mui/material";
import { EyeIcon, PencilIcon } from "@aeriosiq/ui/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  DataAcquisition,
  GetDataAcquisitionsResponse,
} from "@aeriosiq/types";
import { dataAcquisitionKeys } from "./queries.js";

/**
 * SB2-W-02 — claim-eligible set per OQ-SB2-B1(c).
 *
 * NOTE: narrower than the R58-W-09 dropdown workflow role set
 * (`useRoleBearingUsers.WORKFLOW_ROLES`) — `project_manager` and
 * `back_office` are in the dropdown but NOT claim-eligible. Matches
 * the BE LyseClaimService.CLAIM_ELIGIBLE_ROLES exactly.
 */
const CLAIM_ELIGIBLE_ROLE_SET = new Set<string>([
  "platform_admin",
  "tenant_admin",
  "expert_analyst",
  "pre_screener",
]);

const CLAIMABLE_STATES = new Set<string>(["PRE_SCREENING", "EXPERT_ANALYSIS"]);

export type WorkflowRole =
  | "platform_admin"
  | "tenant_admin"
  | "expert_analyst"
  | "pre_screener";

export interface RowActionsCellProps {
  row: DataAcquisition;
  /** Current user's auth_db.users.user_id (resolved from the /me payload). */
  myUserId: string | null | undefined;
  /** Roles from the /me payload. */
  myRoles: readonly string[] | undefined;
  /** Navigates to the analysis page for the given DA id. */
  onOpen: (id: string) => void;
}

type Label = "Edit" | "Claim & Edit" | "View";

interface ClaimResponse {
  data_acquisition: DataAcquisition;
}

interface Claim409Body {
  code?: string;
  reason?: string;
  currentAssigneeId?: string;
  currentAssigneeName?: string;
}

interface Claim403Body {
  code?: string;
  reason?: string;
  requirement?: Record<string, unknown>;
}

function deriveLabel(args: {
  row: DataAcquisition;
  myUserId: string | null | undefined;
  myRoles: readonly string[] | undefined;
}): Label {
  const { row, myUserId, myRoles } = args;
  const isClaimEligible = (myRoles ?? []).some((r) =>
    CLAIM_ELIGIBLE_ROLE_SET.has(r),
  );
  // SB3-CC-G (lyse/v1@6.0.0): unified single-axis `state` per I5 supersedure.
  // CLAIMABLE_STATES must contain the SB3 values (PRE_SCREENING /
  // EXPERT_ANALYSIS) that previously lived on `evaluationState`.
  const stateIsClaimable = CLAIMABLE_STATES.has(row.state);
  if (row.assignedTo && myUserId && row.assignedTo === myUserId) return "Edit";
  if (row.assignedTo === null && isClaimEligible && stateIsClaimable) {
    return "Claim & Edit";
  }
  return "View";
}

export function RowActionsCell({
  row,
  myUserId,
  myRoles,
  onOpen,
}: RowActionsCellProps): ReactElement {
  const api = useApi();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const label = deriveLabel({ row, myUserId, myRoles });

  // SB2-W-02: POST /data-acquisitions/{id}/claim. The new endpoint
  // bundles role-gate + state-gate + locked-by-other 409 + audit row
  // into one call. Mutation type returns the wrapped envelope.
  const claimMutation = useMutation<ClaimResponse>({
    mutationFn: async () => {
      const response = await api.post<ClaimResponse>(
        `/data-acquisitions/${encodeURIComponent(row.id)}/claim`,
        {},
      );
      return response.data;
    },
    onMutate: async () => {
      // Optimistic update — patch the row in the cached list to set
      // assignedTo = me. On 409/403 the onError rollback restores the
      // pre-claim state.
      const queryFamily = dataAcquisitionKeys.lists();
      await qc.cancelQueries({ queryKey: queryFamily });
      const cached = qc.getQueriesData<GetDataAcquisitionsResponse>({
        queryKey: queryFamily,
      });
      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetDataAcquisitionsResponse>(key, {
          ...data,
          items: data.items.map(
            (r): DataAcquisition =>
              r.id === row.id ? { ...r, assignedTo: myUserId ?? null } : r,
          ),
        });
      }
      return cached;
    },
    onError: (err, _vars, ctx) => {
      // Rollback the optimistic patch on any error.
      if (Array.isArray(ctx)) {
        for (const [key, data] of ctx) {
          qc.setQueryData(key, data);
        }
      }
      // Toast mapping per SB2-W-02 §3.3.
      const e = err as unknown as {
        response?: { status?: number; data?: unknown };
        status?: number;
      };
      const status = e.response?.status ?? e.status;
      const body = (e.response?.data ?? {}) as
        | (Claim409Body & Claim403Body)
        | Record<string, unknown>;
      if (status === 409) {
        const b = body as Claim409Body;
        const name = b.currentAssigneeName ?? "another user";
        setError(`Already claimed by ${name}.`);
      } else if (status === 403) {
        const b = body as Claim403Body;
        // Show the structured reason verbatim per AC10 of SB2-W-02.
        setError(
          b.reason
            ? `Cannot claim — ${b.reason.replace(/_/g, " ")}`
            : "You don't have permission to claim this dataset.",
        );
      } else if (status === 404) {
        setError("Dataset not found.");
      } else {
        setError("Couldn't claim. Try again.");
      }
    },
    onSuccess: () => {
      // 200 — navigate. No toast on success per UX flow §3.3.
      onOpen(row.id);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: dataAcquisitionKeys.lists() });
    },
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (label === "Claim & Edit") {
      if (!myUserId) {
        setError("Cannot claim — your account couldn't be resolved.");
        return;
      }
      claimMutation.mutate();
    } else {
      onOpen(row.id);
    }
  };

  return (
    <>
      {/*
        The asset pages' row action (TurbinesPage's "Edit" column) is the
        house style for this job, and Christian asked for it here: a TEXT
        button with a leading glyph, sentence case, no keyline.

        It is also the treatment that fits. An outlined control draws a box
        inside a box — the cell already has grid lines — so P2's "structure is
        drawn" is satisfied twice over, and a bordered 32px button in a 36px
        row leaves 2px of air on each side. A text button carries no keyline
        of its own, so it reads as part of the row rather than as a panel
        floating in it, and the glyph does the work the border was doing:
        distinguishing an action from a value at a glance.

        D26 — the glyph is not decoration. Edit and Claim & Edit both WRITE
        (pencil); View does not (eye). The distinction between the two write
        actions is carried by the label, never by colour alone.
      */}
      <Button
        size="small"
        variant="text"
        startIcon={
          label === "View" ? (
            <EyeIcon fontSize="small" />
          ) : (
            <PencilIcon fontSize="small" />
          )
        }
        onClick={handleClick}
        disabled={claimMutation.isPending}
        data-testid={`row-actions-${row.id}`}
        data-label={label}
        aria-label={`${label} dataset ${String(row.instructionCode)}`}
      >
        {label}
      </Button>
      <Snackbar
        open={error !== null}
        autoHideDuration={4000}
        onClose={() => {
          setError(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => {
            setError(null);
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
